import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import {
  createConnector,
  type GitHubRepoSummary,
  listGitHubBranchesForSetup,
  listGitHubReposForSetup,
  setConnectorScopes,
  triggerSync,
} from '../../api/connectors'
import type { ConnectorResponse } from '../../types/api'
import { FileTreeBrowser } from './FileTreeBrowser'

interface AddConnectorModalProps {
  isOpen: boolean
  projectId: number
  onClose: () => void
  onCreated: (connector: ConnectorResponse) => void
}

type Step = 'provider' | 'configure' | 'scopes' | 'confirm'

const inputClassName =
  'w-full px-3 py-2 bg-harbor-surface-raised border border-harbor-border rounded-lg text-harbor-text placeholder-harbor-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm'

export function AddConnectorModal({
  isOpen,
  projectId,
  onClose,
  onCreated,
}: AddConnectorModalProps) {
  const [step, setStep] = useState<Step>('provider')
  const [isCreating, setIsCreating] = useState(false)

  // GitHub config
  const [pat, setPat] = useState('')
  const [repoOwner, setRepoOwner] = useState('')
  const [repoName, setRepoName] = useState('')
  const [branch, setBranch] = useState('main')
  const [pathPrefix, setPathPrefix] = useState('')
  const [displayName, setDisplayName] = useState('')

  // Repo / branch discovery
  const [repos, setRepos] = useState<GitHubRepoSummary[]>([])
  const [discoveredOrgs, setDiscoveredOrgs] = useState<string[]>([])
  const [orgInput, setOrgInput] = useState('')
  const [repoFilter, setRepoFilter] = useState('')
  const [isLoadingRepos, setIsLoadingRepos] = useState(false)
  const [reposLoadedForPat, setReposLoadedForPat] = useState<string | null>(null)
  const [branches, setBranches] = useState<string[]>([])
  const [isLoadingBranches, setIsLoadingBranches] = useState(false)

  // Scopes
  const [connectorId, setConnectorId] = useState<number | null>(null)
  const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set())

  const selectedFullName = repoOwner && repoName ? `${repoOwner}/${repoName}` : ''
  const reposLoaded = reposLoadedForPat === pat.trim() && repos.length > 0

  const filteredRepos = useMemo(() => {
    const q = repoFilter.trim().toLowerCase()
    if (!q) return repos
    return repos.filter(
      (r) =>
        r.fullName.toLowerCase().includes(q) || (r.description?.toLowerCase().includes(q) ?? false)
    )
  }, [repos, repoFilter])

  function reset() {
    setStep('provider')
    setPat('')
    setRepoOwner('')
    setRepoName('')
    setBranch('main')
    setPathPrefix('')
    setDisplayName('')
    setRepos([])
    setDiscoveredOrgs([])
    setOrgInput('')
    setRepoFilter('')
    setReposLoadedForPat(null)
    setBranches([])
    setConnectorId(null)
    setSelectedPaths(new Set())
    setIsCreating(false)
  }

  function handleClose() {
    reset()
    onClose()
  }

  async function loadRepos(org?: string) {
    const token = pat.trim()
    if (!token) {
      toast.error('Enter a Personal Access Token first')
      return
    }

    setIsLoadingRepos(true)
    try {
      const result = await listGitHubReposForSetup(projectId, token, org)
      if (result.repos.length === 0) {
        setRepos([])
        setDiscoveredOrgs(result.orgs)
        if (!org) setReposLoadedForPat(null)
        const warning = result.warnings[0]
        toast.error(warning ?? 'No repositories found for this token')
        return
      }

      setRepos(result.repos)
      setDiscoveredOrgs(result.orgs)
      setReposLoadedForPat(token)
      setRepoFilter('')
      if (org) setOrgInput(org)

      const stillValid = result.repos.some((r) => r.fullName === selectedFullName)
      if (!stillValid) {
        setRepoOwner('')
        setRepoName('')
        setBranches([])
        setBranch('main')
      }

      toast.success(
        `Loaded ${result.repos.length} repositor${result.repos.length === 1 ? 'y' : 'ies'}${
          org ? ` from ${org}` : ''
        }`
      )
      for (const warning of result.warnings.slice(0, 2)) {
        toast(warning, { icon: '⚠️', duration: 6000 })
      }
    } catch (err) {
      if (!org) {
        setRepos([])
        setReposLoadedForPat(null)
        setDiscoveredOrgs([])
      }
      toast.error(err instanceof Error ? err.message : 'Failed to load repositories')
    } finally {
      setIsLoadingRepos(false)
    }
  }

  async function handleLoadRepos() {
    await loadRepos()
  }

  async function handleLoadOrgRepos() {
    const org = orgInput.trim()
    if (!org) {
      toast.error('Enter an organization name (e.g. Kovaad-Technologies)')
      return
    }
    await loadRepos(org)
  }

  async function handleSelectRepo(fullName: string) {
    const repo = repos.find((r) => r.fullName === fullName)
    if (!repo) {
      setRepoOwner('')
      setRepoName('')
      setBranches([])
      return
    }

    setRepoOwner(repo.owner)
    setRepoName(repo.name)
    setBranch(repo.defaultBranch || 'main')
    if (!displayName.trim()) {
      setDisplayName(repo.fullName)
    }

    setIsLoadingBranches(true)
    setBranches([])
    try {
      const loaded = await listGitHubBranchesForSetup(projectId, pat.trim(), repo.owner, repo.name)
      setBranches(loaded)
      if (loaded.length > 0 && !loaded.includes(repo.defaultBranch)) {
        setBranch(loaded[0] ?? repo.defaultBranch)
      } else if (repo.defaultBranch) {
        setBranch(repo.defaultBranch)
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load branches')
    } finally {
      setIsLoadingBranches(false)
    }
  }

  // Clear discovered repos when PAT changes after a load
  useEffect(() => {
    if (reposLoadedForPat && pat.trim() !== reposLoadedForPat) {
      setRepos([])
      setDiscoveredOrgs([])
      setOrgInput('')
      setReposLoadedForPat(null)
      setBranches([])
      setRepoFilter('')
    }
  }, [pat, reposLoadedForPat])

  async function handleCreateConnector() {
    if (!repoOwner || !repoName) {
      toast.error('Select a repository from the list')
      return
    }

    setIsCreating(true)
    try {
      const configJson = JSON.stringify({
        owner: repoOwner,
        repo: repoName,
        branch: branch || 'main',
        ...(pathPrefix ? { pathPrefix } : {}),
      })

      const connector = await createConnector(projectId, {
        provider: 'github',
        displayName: displayName || `${repoOwner}/${repoName}`,
        credentials: pat,
        configJson,
      })

      setConnectorId(connector.id)
      setStep('scopes')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create connector')
    } finally {
      setIsCreating(false)
    }
  }

  async function handleFinish() {
    if (!connectorId) return

    setIsCreating(true)
    try {
      if (selectedPaths.size > 0) {
        const scopes = Array.from(selectedPaths).map((pathId) => ({
          externalIdentifier: pathId,
          scopeType: pathId.startsWith('dir:') ? 'folder' : 'file',
        }))
        await setConnectorScopes(connectorId, scopes)
      }

      await triggerSync(connectorId)

      toast.success('Connector created and sync started')

      const { getConnector } = await import('../../api/connectors')
      const updated = await getConnector(connectorId)
      onCreated(updated)
      reset()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to complete setup')
    } finally {
      setIsCreating(false)
    }
  }

  function canProceedFromConfigure(): boolean {
    return Boolean(pat.trim() && repoOwner && repoName)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-harbor-surface border border-harbor-border rounded-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-harbor-border">
          <div>
            <h2 className="text-lg font-semibold text-harbor-text">Add Connector</h2>
            <p className="text-sm text-harbor-text-secondary mt-0.5">
              {step === 'provider' && 'Choose a data source'}
              {step === 'configure' && 'Configure GitHub connection'}
              {step === 'scopes' && 'Select files to sync'}
              {step === 'confirm' && 'Review and confirm'}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-harbor-text-muted hover:text-harbor-text transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Step indicators */}
        <div className="flex items-center gap-2 px-6 pt-4">
          {(['provider', 'configure', 'scopes', 'confirm'] as Step[]).map((s, i) => {
            const isActive = s === step
            const isPast = ['provider', 'configure', 'scopes', 'confirm'].indexOf(step) > i
            return (
              <div key={s} className="flex items-center gap-2">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                    isActive
                      ? 'bg-emerald-600 text-white'
                      : isPast
                        ? 'bg-emerald-600/30 text-emerald-400'
                        : 'bg-harbor-surface-raised text-harbor-text-muted'
                  }`}
                >
                  {isPast ? (
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : (
                    i + 1
                  )}
                </div>
                {i < 3 && (
                  <div
                    className={`w-8 h-px ${isPast ? 'bg-emerald-600/50' : 'bg-harbor-border'}`}
                  />
                )}
              </div>
            )
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Provider selection */}
          {step === 'provider' && (
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => {
                  setStep('configure')
                }}
                className="flex flex-col items-center gap-3 p-6 bg-harbor-surface-raised border border-harbor-border rounded-xl hover:border-emerald-500/50 hover:bg-emerald-500/5 transition-all group"
              >
                <svg
                  className="w-10 h-10 text-harbor-text group-hover:text-emerald-400 transition-colors"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
                </svg>
                <div className="text-center">
                  <p className="text-sm font-medium text-harbor-text">GitHub</p>
                  <p className="text-xs text-harbor-text-muted mt-0.5">
                    Sync files from a repository
                  </p>
                </div>
              </button>

              <div className="flex flex-col items-center gap-3 p-6 bg-harbor-surface-raised/50 border border-harbor-border rounded-xl opacity-50 cursor-not-allowed">
                <svg
                  className="w-10 h-10 text-harbor-text-muted"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 0L1.608 6v12L12 24l10.392-6V6L12 0zm-1.2 18L4.8 12l2.4-4.2L12 12l-1.2 6zm6.6-6L15 16.2 12 12l5.4-4.2z" />
                </svg>
                <div className="text-center">
                  <p className="text-sm font-medium text-harbor-text-secondary">Google Drive</p>
                  <p className="text-xs text-harbor-text-muted mt-0.5">Coming soon</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: GitHub configuration */}
          {step === 'configure' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-harbor-text-secondary mb-1">
                  Display Name
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="e.g. Main Documentation"
                  className={inputClassName}
                />
                <p className="text-xs text-harbor-text-muted mt-1">
                  Optional. Defaults to owner/repo.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-harbor-text-secondary mb-1">
                  Personal Access Token
                </label>
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={pat}
                    onChange={(e) => setPat(e.target.value)}
                    placeholder="ghp_xxxxxxxxxxxx"
                    className={inputClassName}
                  />
                  <button
                    type="button"
                    onClick={() => void handleLoadRepos()}
                    disabled={!pat.trim() || isLoadingRepos}
                    className="shrink-0 px-3 py-2 text-sm font-medium rounded-lg border border-harbor-border text-harbor-text hover:bg-harbor-surface-raised disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isLoadingRepos ? 'Loading…' : reposLoaded ? 'Reload' : 'Load repos'}
                  </button>
                </div>
                <p className="text-xs text-harbor-text-muted mt-1">
                  Classic PAT: <code className="text-harbor-text-secondary">repo</code> +{' '}
                  <code className="text-harbor-text-secondary">read:org</code>. Fine-grained: grant
                  the org repositories (and authorize SSO if required).
                  <a
                    href="https://github.com/settings/tokens"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-1 text-emerald-400 hover:text-emerald-300"
                  >
                    Create token
                  </a>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-harbor-text-secondary mb-1">
                  Organization
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    list="github-orgs"
                    value={orgInput}
                    onChange={(e) => setOrgInput(e.target.value)}
                    placeholder="e.g. Kovaad-Technologies"
                    className={inputClassName}
                    disabled={!pat.trim()}
                  />
                  <datalist id="github-orgs">
                    {discoveredOrgs.map((org) => (
                      <option key={org} value={org} />
                    ))}
                  </datalist>
                  <button
                    type="button"
                    onClick={() => void handleLoadOrgRepos()}
                    disabled={!pat.trim() || !orgInput.trim() || isLoadingRepos}
                    className="shrink-0 px-3 py-2 text-sm font-medium rounded-lg border border-harbor-border text-harbor-text hover:bg-harbor-surface-raised disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isLoadingRepos ? 'Loading…' : 'Load org'}
                  </button>
                </div>
                <p className="text-xs text-harbor-text-muted mt-1">
                  Org repos often need an explicit org name. Use the exact GitHub org login (e.g.{' '}
                  <code className="text-harbor-text-secondary">Kovaad-Technologies</code>).
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-harbor-text-secondary mb-1">
                  Repository
                </label>
                {reposLoaded ? (
                  <div className="space-y-2">
                    {repos.length > 8 && (
                      <input
                        type="search"
                        value={repoFilter}
                        onChange={(e) => setRepoFilter(e.target.value)}
                        placeholder="Filter repositories…"
                        className={inputClassName}
                      />
                    )}
                    <select
                      value={selectedFullName}
                      onChange={(e) => void handleSelectRepo(e.target.value)}
                      className={inputClassName}
                    >
                      <option value="">Select a repository…</option>
                      {filteredRepos.map((r) => (
                        <option key={r.fullName} value={r.fullName}>
                          {r.fullName}
                          {r.private ? ' (private)' : ''}
                        </option>
                      ))}
                    </select>
                    {filteredRepos.length === 0 && (
                      <p className="text-xs text-harbor-text-muted">
                        No repositories match your filter.
                      </p>
                    )}
                    <p className="text-xs text-harbor-text-muted">
                      One repository per connector. {repos.length} available.
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-harbor-text-muted py-2 px-3 bg-harbor-surface-raised border border-harbor-border rounded-lg">
                    Enter a token and click{' '}
                    <span className="text-harbor-text-secondary">Load repos</span>, or enter an org
                    name and click <span className="text-harbor-text-secondary">Load org</span>.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-harbor-text-secondary mb-1">
                    Branch
                  </label>
                  {branches.length > 0 ? (
                    <select
                      value={branch}
                      onChange={(e) => setBranch(e.target.value)}
                      disabled={isLoadingBranches}
                      className={inputClassName}
                    >
                      {branches.map((b) => (
                        <option key={b} value={b}>
                          {b}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={branch}
                      onChange={(e) => setBranch(e.target.value)}
                      placeholder={isLoadingBranches ? 'Loading branches…' : 'main'}
                      disabled={isLoadingBranches}
                      className={inputClassName}
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-harbor-text-secondary mb-1">
                    Path Prefix <span className="text-harbor-text-muted">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={pathPrefix}
                    onChange={(e) => setPathPrefix(e.target.value)}
                    placeholder="docs/"
                    className={inputClassName}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Scope selection */}
          {step === 'scopes' && connectorId && (
            <div className="space-y-3">
              <p className="text-sm text-harbor-text-secondary">
                Select folders or files to sync. Leave empty to sync all files in the repository.
              </p>
              <FileTreeBrowser
                connectorId={connectorId}
                selectedPaths={selectedPaths}
                onSelectionChange={setSelectedPaths}
              />
            </div>
          )}

          {/* Step 4: Confirm */}
          {step === 'confirm' && (
            <div className="space-y-4">
              <div className="bg-harbor-surface-raised rounded-lg p-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-harbor-text-secondary">Provider</span>
                  <span className="text-harbor-text">GitHub</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-harbor-text-secondary">Repository</span>
                  <span className="text-harbor-text font-mono text-xs">
                    {repoOwner}/{repoName}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-harbor-text-secondary">Branch</span>
                  <span className="text-harbor-text font-mono text-xs">{branch}</span>
                </div>
                {pathPrefix && (
                  <div className="flex justify-between text-sm">
                    <span className="text-harbor-text-secondary">Path</span>
                    <span className="text-harbor-text font-mono text-xs">{pathPrefix}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-harbor-text-secondary">Selected scopes</span>
                  <span className="text-harbor-text">
                    {selectedPaths.size === 0
                      ? 'All files'
                      : `${selectedPaths.size} item${selectedPaths.size !== 1 ? 's' : ''}`}
                  </span>
                </div>
              </div>
              <p className="text-xs text-harbor-text-muted">
                A sync will start immediately after creation. Documents will be indexed and
                searchable once complete.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-harbor-border">
          <button
            onClick={
              step === 'provider'
                ? handleClose
                : step === 'configure'
                  ? () => setStep('provider')
                  : step === 'scopes'
                    ? () => setStep('configure')
                    : () => setStep('scopes')
            }
            className="px-4 py-2 text-sm text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg transition-colors"
          >
            {step === 'provider' ? 'Cancel' : 'Back'}
          </button>

          <div className="flex gap-3">
            {step === 'configure' && (
              <button
                onClick={handleCreateConnector}
                disabled={!canProceedFromConfigure() || isCreating}
                className="px-4 py-2 bg-emerald-600 text-harbor-text text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                {isCreating ? 'Connecting...' : 'Next'}
              </button>
            )}
            {step === 'scopes' && (
              <button
                onClick={() => setStep('confirm')}
                className="px-4 py-2 bg-emerald-600 text-harbor-text text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Next
              </button>
            )}
            {step === 'confirm' && (
              <button
                onClick={handleFinish}
                disabled={isCreating}
                className="px-4 py-2 bg-emerald-600 text-harbor-text text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                {isCreating ? 'Creating...' : 'Create & Sync'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
